import { show as openIntercomMessenger } from '@intercom/messenger-js-sdk';
import { Alert, Box, Link } from '@televet/kibble-ui';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { ActionEffectType } from '@televet/shared-types';
import React, { useCallback, useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';
import { useAuth } from 'shared/providers/AuthProvider';
import {
  PermissionType,
  useGetAutomationsSelectionListQuery,
  useSaveWorkflowEventSettingsMutation,
  WorkflowEventAction
} from 'shared/types/graphql';
import { TypedAutomationSetting } from '../../types';
import AutomationTaskTable, { AutomationTaskRow } from './components/AutomationTaskTable';
import { createCurriedAssigneeUpdater } from './utils';

const AutomationTaskAssigneeSection = (): JSX.Element => {
  const { currentClinicId, userHasPermission } = useClinicUser();
  const { isImpersonating } = useAuth();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);
  const hasPermission = userHasPermission(PermissionType.WriteClinic);
  const { data, loading } = useGetAutomationsSelectionListQuery({
    variables: { clinicId: currentClinicId || '' },
  });
  const [saveWorkflowEventSettings] = useSaveWorkflowEventSettingsMutation();

  const automationTaskRows = useMemo(() => {
    const settings = data?.findManyWorkflowEventSetting as TypedAutomationSetting[] | undefined;
    const rows: AutomationTaskRow[] = [];

    if (!settings) return rows;

    for (const setting of settings) {
      if (!setting.isPublished) continue;

      for (const workflowAction of setting.actions) {
        const { config } = workflowAction;
        if (!config?.buttons) continue;

        for (const button of config.buttons) {
          const taskEffect = button.actionEffects?.find((e) => e?.type === ActionEffectType.CreateTask);
          if (!taskEffect) continue;

          const getUpdatedAction = createCurriedAssigneeUpdater(workflowAction, config, button);
          rows.push({
            taskName: taskEffect.taskName || '',
            assigneeIds: taskEffect.assigneeIds || [],
            automationName: setting.name || '',
            settingId: setting.id,
            getUpdatedAction,
          });
        }
      }
    }

    return rows;
  }, [data]);

  const handleSaveUpdatedAction = useCallback(
    (settingId: string, updatedAction: Partial<WorkflowEventAction>) => {
      saveWorkflowEventSettings({
        variables: {
          data: {
            actions: {
              update: [
                {
                  data: { config: updatedAction.config },
                  where: { id: updatedAction.id || '' },
                },
              ],
            },
          },
          where: { id: settingId },
        },
      });
    },
    [saveWorkflowEventSettings],
  );


  const handleContactSupportClick = useCallback((): void => {
    if (isImpersonating) {
      return;
    }
    if (isStandardClinic) {
      window?.zE?.('messenger', 'open');
    } else {
      openIntercomMessenger();
    }
  }, [isImpersonating, isStandardClinic]);

  return (
    <Box>
      <Box mb={5}>
        <Heading size="sm" mb={2}>Automations</Heading>
        <Text as="p" size="sm" color="text.subtle">Set any automatic assignees for tasks created from an automation.</Text>
      </Box>
      {loading ? <Spinner size="sm" /> : <>{!automationTaskRows.length ? (
        <Alert
          status='info'
          width="auto"
          hideCloseButton
          justifySelf="flex-start"
          title={
            <Box>
              <Text as="p" size="sm" fontWeight="bold">You don’t have any automations set up to generate tasks.</Text>
              <Text as="p" size="sm" fontWeight="normal">Want tasks assigned automatically from automations?{' '}
                <Link onClick={handleContactSupportClick} size="sm">Contact Otto Support</Link></Text>
            </Box>
          }
        />
      ) : (
          <AutomationTaskTable data={automationTaskRows} onUpdateAction={handleSaveUpdatedAction} hasPermission={hasPermission} />
        )}</>}
    </Box>
  );
};

export default AutomationTaskAssigneeSection;


